import React, { CSSProperties, useEffect, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'
import clsx from 'clsx'
import noop from 'lib/function/noop'

const Root = styled.div`
  position: sticky;

  /* Don't be sticky when printing */
  @media print {
    position: unset;
  }
`

const Detector = styled.div`
  position: absolute;
  height: 1px;
  width: 1px;
  margin: 0 !important;
`

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  top?: CSSProperties['top']
  onStuckChange?: (isStuck: boolean) => void
  ref?: React.Ref<HTMLDivElement | null>
}

function StickyWithStatus(props: Props) {
  const {
    children,
    className,
    top = '0px',
    onStuckChange = noop,
    style,
    ref,
    ...restProps
  } = props

  const topStr = typeof top === 'number' ? `${top}px` : top

  const rootStyle = useMemo(() => ({ ...style, top }), [style, top])
  const detectorStyle = useMemo(() => ({ top: `calc(-1 * ${topStr} - 2px)` }), [topStr])

  const { ref: detectorRef, inView, entry } = useInView({ rootMargin: '0px' })

  let stuck = false
  if (entry) {
    stuck = entry.boundingClientRect.bottom <= 0 && !inView
  }

  useEffect(() => {
    onStuckChange(stuck)
  }, [stuck, onStuckChange])

  const classes = clsx(className, { stuck })

  return <Root
    {...restProps}
    ref={ref}
    className={classes}
    style={rootStyle}
  >
    {children}
    <Detector style={detectorStyle} ref={detectorRef} />
  </Root>
}

export default React.memo(StickyWithStatus)
